import { getTaskStartText } from '@frontend/api-client';
import type { TaskDetail } from '@frontend/api-types';
import { match } from '@frontend/duck-tape';
import { makeSavedFileList } from '@frontend/forms';
import { useEffect, useTempFeatureFlag } from '@frontend/react';
import type { InitialQuestionWithAnswer } from '@frontend/react-domain';
import { getIQTitle } from '@frontend/react-domain';
import { FilePreviewsList, Icon, Text, XStack, YStack } from '@frontend/web-react';
import { SectionCard } from '@frontend/web/components/DomainSpecific/DraftTasks';
import { TaskStatisticsCard } from '@frontend/web/components/DomainSpecific/Tasks/TaskStatisticsCard';
import { useAnalytics } from '@frontend/web/hooks';

export const TaskDetailSummaryTab = ({ task }: { task: TaskDetail }) => {
  const { track } = useAnalytics();
  const shouldShowTaskStatisticsFeature = useTempFeatureFlag('should_show_task_statistics');
  const taskStartText = getTaskStartText(task);
  const shouldShowTaskStatistics = Boolean(
    shouldShowTaskStatisticsFeature && task?.taskStatistics?.totalTaskTimeSeconds,
  );

  useEffect(() => {
    if (shouldShowTaskStatistics) {
      track({ data: { taskId: task.id }, event: 'taskStatisticsCardViewed', topic: 'tasks' });
    }
  }, [shouldShowTaskStatistics, task.id, track]);

  return (
    <YStack className="w-full items-center overflow-scroll">
      <YStack className="gap-md max-w-screen-md w-full">
        <YStack className="gap-md p-md">
          <YStack className="lg:col-span-2 lg:col-start-3 md:col-span-2">
            <YStack className="gap-y-md">
              {shouldShowTaskStatistics && task.taskStatistics ? (
                <TaskStatisticsCard taskStatistics={task.taskStatistics} />
              ) : null}
              <SectionCard icon="IconAlignLeft" title="Description">
                <YStack className="gap-y-md">
                  <YStack className="gap-y-md">
                    <Text color="secondary">{task.description}</Text>
                    <FilePreviewsList
                      files={makeSavedFileList(task.attachments, (a) => ({ ...a, key: a.id, url: a.contentUrl }))}
                    />
                  </YStack>
                  <XStack gapX="sm" justifyContent="start">
                    <Icon className="self-start" color="secondary" name="IconInfoCircleFilled" size={14} />
                    <Text color="secondary" type="p-sm">
                      Changing the description will reset your progress and {"you'll"} need to go through the questions
                      again.
                    </Text>
                  </XStack>
                </YStack>
              </SectionCard>
              {task.taskSummary ? (
                <SectionCard icon="IconFileDescription" title="Task Summary">
                  <Text color="secondary">{task.taskSummary.summary}</Text>
                </SectionCard>
              ) : null}
              <SectionCard icon="IconStack" title="Detail">
                <YStack className="gap-y-md">
                  {task?.aiQuestionnaire?.responses?.map((iq) => {
                    return (
                      <YStack className="gap-y-xs" key={iq.questionId}>
                        <Text type="h6">{getIQTitle(iq)}</Text>
                        {match(iq)
                          .with({ type: 'multi_choice' }, (iq) => {
                            return iq.answer.map((a) => (
                              <Text color="secondary" key={a}>
                                - {a}
                              </Text>
                            ));
                          })
                          .with({ type: 'text' }, (iq: InitialQuestionWithAnswer & { type: 'text' }) => (
                            <Text color="secondary">{iq.answer || 'Not answered'}</Text>
                          ))
                          .with({ type: 'secure_note' }, (iq) => (
                            <Text color="secondary">{iq.secureNoteId ? 'Provided ✅' : 'Skipped ❌'}</Text>
                          ))
                          .exhaustive()}
                      </YStack>
                    );
                  })}
                </YStack>
              </SectionCard>
            </YStack>
          </YStack>
          <YStack className="lg:col-span-2 md:col-span-2">
            {taskStartText ? (
              <SectionCard title="Task starts">
                <YStack className="gap-y-md">
                  <YStack>
                    <Text color="secondary" type="p-sm">
                      {taskStartText}
                    </Text>
                  </YStack>
                </YStack>
              </SectionCard>
            ) : null}
          </YStack>
        </YStack>
      </YStack>
    </YStack>
  );
};
